import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTestimonials } from "@/hooks/useTestimonials";
import PhotoDemo, { BoxBrackets, BracketMark } from "./PhotoDemo";
import SiteFrame from "./SiteFrame";
import { BIBS, BUSINESS_HERO, BUSINESS_PHOTOS } from "./images";
import { APP_PATH, LOGIN_PATH, REGISTER_PATH } from "./paths";
import { PhoneIcon, ProductEyebrow } from "./products";
import { StudioAppSection, StudioPillars, StudioPlans } from "./StudioSections";
import { AMBER_DISPLAY, BAND, BTN_OUTLINE, BTN_OUTLINE_MD, BTN_PRIMARY, CARD, EYEBROW, FOCUS, H1, H2, HERO_FIGURE, HERO_TEXT, LEAD, SECTION_Y, TEXT_LINK, WRAP } from "./ui";

// /studio: Photohawk Studio, for photographers and event businesses (it was /events; /events and /business are
// 301s here). TODAY's home page content (hero.*, audience.*, features.*, howItWorks.*, the testimonials and the
// stats; keys shared with src/pages/Index.tsx, so every site language keeps working), in the new look, plus what's
// in Studio (galleries, selling, Find your photos, live upload), the Studio app (#studio-app: coming November 2026,
// in the browser today) and the Studio plans
// (#pricing: Lite, Pro, Max), from src/pages/home/StudioSections.tsx. Light, in the dashboard's tokens: white
// sections alternating with its muted band (src/pages/home/ui.ts).

const FEATURE_KEYS = ["bibDetection", "facialRecognition", "colourDetection", "secureCheckout", "lightningFast", "customBranding"] as const;
const STEPS = ["1", "2", "3", "4"] as const;


const StudioPage = () => {
  const { t } = useTranslation();
  const { testimonials } = useTestimonials();
  const b = (key: string, opts?: Record<string, string | number>): string => t(`home.business.${key}`, opts ?? {});

  const stats = [
    { value: "75M+", label: t("hero.photosUploaded") },
    { value: "185M+", label: t("hero.peopleDetected") },
    { value: "450+", label: t("hero.members") },
    { value: "29", label: t("hero.countries") },
  ];

  const audiences = [
    { id: "photographers", link: "/for-photographers" },
    { id: "participants", link: "/for-participants" },
  ] as const;

  return (
    <SiteFrame page="studio" path="studio">
      {/* HERO */}
      <section aria-labelledby="business-title">
        <div className={`${WRAP} flex flex-wrap items-center gap-x-14 gap-y-10 pb-12 pt-[clamp(32px,5vw,80px)]`}>
          <div className={HERO_TEXT}>
            <ProductEyebrow product="studio" text={t("home.studio.eyebrow")} />
            <h1 id="business-title" className={H1}>
              {t("hero.titleFor")} <span style={{ color: AMBER_DISPLAY }}>{t("hero.sharing")}</span> {t("hero.and")}{" "}
              <span style={{ color: AMBER_DISPLAY }}>{t("hero.selling")}</span> {t("hero.photos")}
            </h1>
            <p className="m-0 max-w-[32em] text-[clamp(17px,1.5vw,20px)] leading-relaxed text-muted-foreground">{t("hero.subtitle")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to={REGISTER_PATH} className={BTN_PRIMARY}>
                {t("home.startFree")}
              </Link>
              <a href="#studio-app" className={BTN_OUTLINE} data-testid="studio-hero-app">
                {t("home.doors.studio.appSoon")}
              </a>
            </div>
            <p className="m-0 text-[15px] font-semibold text-foreground" data-testid="studio-hero-platforms">
              {t("home.doors.studio.platforms")}
            </p>
            <p className="m-0 flex flex-wrap items-center gap-x-5 gap-y-1 text-[15px] text-muted-foreground">
              <span>
                {t("home.doors.studio.signInLead")}{" "}
                <Link to={LOGIN_PATH} className={`rounded-md font-semibold text-foreground underline underline-offset-4 ${FOCUS}`}>
                  {t("home.doors.studio.signIn")}
                </Link>
              </span>
              <a href="#pricing" className={TEXT_LINK}>
                {t("home.studio.seePlans")} <span aria-hidden="true">→</span>
              </a>
            </p>
          </div>
          <figure className={`${HERO_FIGURE} pb-7`}>
            <div className="relative aspect-[1023/684] w-full overflow-hidden rounded-xl shadow-lg">
              <img
                src={BUSINESS_HERO.src}
                srcSet={BUSINESS_HERO.srcSet}
                sizes="(min-width: 1024px) 640px, 100vw"
                width={BUSINESS_HERO.width}
                height={BUSINESS_HERO.height}
                alt={b("heroAlt")}
                className="block h-full w-full object-cover"
              />
              {BUSINESS_HERO.faces.map((f, i) => (
                <BoxBrackets key={`f${i}`} box={f.box} arm="28%" delay={f.delay} />
              ))}
              {BUSINESS_HERO.bibs.map((x) => (
                <span key={x.bib}>
                  <BoxBrackets box={x.box} color="#FFFFFF" arm="28%" delay={x.delay} />
                  <span
                    aria-hidden="true"
                    className="absolute rounded bg-white px-2 py-[3px] text-xs font-bold text-[#121721]"
                    style={{ left: `${x.box[0]}%`, top: `${x.box[1] + x.box[3] + 1.4}%` }}
                  >
                    {x.bib}
                  </span>
                </span>
              ))}
            </div>
            <figcaption className="absolute bottom-0 left-0 flex max-w-[86%] items-center gap-3 rounded-xl border border-border bg-card py-3 pl-3 pr-4 text-foreground shadow-lg sm:left-[-12px]">
              <img src={BUSINESS_HERO.face} alt="" width={46} height={46} className="h-[46px] w-[46px] flex-none rounded-full object-cover" />
              <span className="flex min-w-0 flex-col gap-px">
                <strong className="text-[15px]">{b("heroCaption")}</strong>
                <span className="text-[13px] text-muted-foreground">{b("heroCaptionSub")}</span>
              </span>
            </figcaption>
          </figure>
        </div>
        <div className={`${WRAP} pb-12 pt-10`}>
          <dl className="m-0 grid grid-cols-2 gap-6 border-t border-border pt-7 sm:grid-cols-4" data-testid="business-stats">
            {stats.map((s) => (
              <div key={s.value} className="flex flex-col-reverse gap-1">
                <dt className="text-sm text-muted-foreground">{s.label}</dt>
                <dd className="m-0 font-display text-4xl font-bold tracking-[-0.02em] tabular-nums">{s.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* WHAT'S IN STUDIO */}
      <StudioPillars />

      {/* AUDIENCE */}
      <section aria-labelledby="audience-heading" className={`${WRAP} flex flex-col gap-9 pt-[clamp(64px,8vw,104px)]`}>
        <div className="flex max-w-[760px] flex-col gap-3.5">
          <h2 id="audience-heading" className={H2}>
            {t("audience.heading")} <span style={{ color: AMBER_DISPLAY }}>{t("audience.headingHighlight")}</span>
          </h2>
          <p className="m-0 text-lg leading-relaxed text-muted-foreground">{t("audience.subtitle")}</p>
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
          {audiences.map((a) => (
            <div key={a.id} className={`${CARD} flex flex-col gap-3.5 p-[clamp(22px,3vw,30px)]`}>
              <h3 className="m-0 font-display text-[26px] font-bold">{t(`audience.${a.id}.title`)}</h3>
              <p className="m-0 text-base leading-relaxed text-muted-foreground">{t(`audience.${a.id}.description`)}</p>
              <ul className="m-0 mt-1.5 grid list-none grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-x-5 gap-y-2.5 p-0 text-[15px]">
                {[1, 2, 3, 4].map((n) => (
                  <li key={n} className="flex items-center gap-2.5">
                    <BracketMark size={14} />
                    {t(`audience.${a.id}.feature${n}`)}
                  </li>
                ))}
              </ul>
              <Link to={a.link} className={`${TEXT_LINK} mt-1 self-start`}>
                {b("learnMore")} <span aria-hidden="true">→</span>
                <span className="sr-only">: {t(`audience.${a.id}.title`)}</span>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" aria-labelledby="features-heading" className={`${WRAP} flex scroll-mt-4 flex-col gap-9 pt-[clamp(64px,8vw,104px)]`}>
        <div className="flex flex-wrap items-end justify-between gap-x-12 gap-y-4">
          <div className="flex max-w-[640px] flex-col gap-3">
            <span className={EYEBROW}>{t("features.badge")}</span>
            <h2 id="features-heading" className={H2}>
              {t("features.heading")}
            </h2>
          </div>
          <p className={`${LEAD} max-w-[30em]`}>{t("features.subtitle")}</p>
        </div>
        <ul className="m-0 grid list-none grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border p-0 shadow-sm sm:grid-cols-2 lg:grid-cols-3">
          {FEATURE_KEYS.map((f) => (
            <li key={f} className="flex flex-col gap-2.5 bg-card p-7">
              <h3 className="m-0 font-display text-[21px] font-semibold">{t(`features.${f}.title`)}</h3>
              <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{t(`features.${f}.description`)}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* DEMO */}
      <PhotoDemo
        idPrefix="business-demo"
        eyebrow={b("demo.eyebrow")}
        heading={b("demo.heading")}
        intro={b("demo.text")}
        chips={BIBS.map((n) => ({ id: n, label: n }))}
        chipsLabel={b("demo.chipsLabel")}
        initial="969"
        photos={BUSINESS_PHOTOS}
        altOf={(p) => b(`demo.alt.${p.id}`)}
        result={(chip, count, total) => b("demo.result", { bib: chip.label, count, total })}
        chipShape="key"
      />

      {/* THE STUDIO APP */}
      <StudioAppSection />

      {/* HOW IT WORKS */}
      <section aria-labelledby="how-heading">
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-9`}>
          <div className="flex flex-col gap-3">
            <span className={EYEBROW}>{t("howItWorks.badge")}</span>
            <h2 id="how-heading" className={H2}>
              {t("howItWorks.heading")}
            </h2>
            <p className={`${LEAD} max-w-[40em]`}>{t("howItWorks.subtitle")}</p>
          </div>
          <ol className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-8 p-0">
            {STEPS.map((n, i) => (
              <li key={n} className={`flex flex-col gap-2.5 border-t-2 pt-[18px] ${i === 0 ? "border-[#EA8A23]" : "border-border"}`}>
                <span className={`font-display font-bold ${i === 0 ? "text-accent-foreground" : "text-muted-foreground"}`} aria-hidden="true">
                  0{n}
                </span>
                <h3 className="m-0 font-display text-[21px] font-semibold">{t(`howItWorks.step${n}Title`)}</h3>
                <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{t(`howItWorks.step${n}Desc`)}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TESTIMONIALS (the same Supabase list as today; hidden while empty) */}
      {testimonials.length > 0 && (
        <section id="reviews" aria-labelledby="love-heading" className={`${BAND} border-y border-border`}>
          <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-8`}>
          <div className="flex max-w-[700px] flex-col gap-3">
            <span className={EYEBROW}>{t("testimonials.badge")}</span>
            <h2 id="love-heading" className={H2}>
              {t("testimonials.heading")}
            </h2>
            <p className={LEAD}>{t("testimonials.subtitle")}</p>
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(280px,100%),1fr))] gap-5">
            {testimonials.map((q) => (
              <figure key={q.id} className={`${CARD} m-0 flex flex-col gap-[18px] p-7`} data-testid="testimonial">
                {q.rating > 0 && (
                  <span className="text-accent-foreground" aria-label={`${q.rating} / 5`}>
                    {"★".repeat(Math.min(5, q.rating))}
                  </span>
                )}
                <blockquote className="m-0 text-[17px] leading-relaxed">“{q.quote}”</blockquote>
                <figcaption className="mt-auto text-sm text-muted-foreground">
                  <strong className="text-foreground">{q.name}</strong> · {q.role} · {q.plan} {t("testimonials.plan")}
                </figcaption>
              </figure>
            ))}
          </div>
          </div>
        </section>
      )}

      {/* THE STUDIO PLANS (#pricing): Lite, Pro, Max. Only Studio has pricing on the site. */}
      <StudioPlans />

      {/* CROSS-LINK */}
      <section aria-labelledby="business-cross" className={`${BAND} border-t border-border`}>
        <div className={`${WRAP} flex flex-wrap items-center justify-between gap-x-8 gap-y-3.5 py-7`}>
          <p className="m-0 flex items-center gap-3.5 text-base leading-normal">
            <span className="flex-none text-accent-foreground">
              <PhoneIcon size={26} />
            </span>
            <span>
              <strong id="business-cross">{b("cross.title")}</strong> {b("cross.text")}
            </span>
          </p>
          <Link to={APP_PATH} className={BTN_OUTLINE_MD}>
            {b("cross.button")}
          </Link>
        </div>
      </section>
    </SiteFrame>
  );
};

export default StudioPage;
